import React, { useState } from 'react'
import { Table, Checkbox, Button } from 'antd'
import moment from 'moment'
import { lzDownloadBlobFile } from '../../helpers/commons/ApiWrapper'
import { getExcelDateExportConfig, useMasterDateTimeFormats } from 'src/helpers/commons/masterDateFormat'

export default function DetailedDataReportTable({ reportData, columns, defaultCheckedList, canExport = true, tableScrollX = 4000 }) {
  const [checkedList, setCheckedList] = useState(defaultCheckedList)
  const { dateFormat } = useMasterDateTimeFormats()

  const options = columns.map(({ key, title }) => ({
    label: title,
    value: key,
  }))

  const newColumns = columns.map((item) => ({
    ...item,
    ellipsis: item.ellipsis ?? true,
    hidden: !checkedList.includes(item.key),
  }))

  const visibleColumns = columns.filter((col) => checkedList.includes(col.key))

  const handleSelect = (event) => {
    if (event.target.checked) {
      setCheckedList(options.map((item) => item.value))
    } else {
      setCheckedList([])
    }
  }

  const getCellValue = (col, record) => {
    if (col.render) {
      const raw = col.dataIndex ? record[col.dataIndex] : record
      const rendered = col.dataIndex
        ? col.render(record[col.dataIndex], record)
        : col.render(record)
      if (typeof rendered === 'string' || typeof rendered === 'number') return rendered
      return col.dataIndex ? record[col.dataIndex] ?? '-' : '-'
    }
    return col.dataIndex ? record[col.dataIndex] ?? '-' : '-'
  }

  const escapeExcelValue = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  const getExcelCellHtml = (col, record) => {
    const rawValue = col.dataIndex ? record[col.dataIndex] : null
    const displayValue = getCellValue(col, record)

    if (col.isDate) {
      const exportConfig = getExcelDateExportConfig(rawValue, dateFormat)

      if (exportConfig.type === 'empty') {
        return '<td></td>'
      }

      if (exportConfig.type === 'str') {
        return `<td x:str>${escapeExcelValue(exportConfig.value)}</td>`
      }

      return `<td style="mso-number-format:'${exportConfig.numberFormat}';" x:num>${exportConfig.value}</td>`
    }

    return `<td>${escapeExcelValue(displayValue)}</td>`
  }

  const exportToExcel = () => {
    const headerRow = visibleColumns.map((col) => `<th>${escapeExcelValue(col.title)}</th>`).join('')
    const rows = reportData
      .map(
        (record) =>
          `<tr>${visibleColumns.map((col) => getExcelCellHtml(col, record)).join('')}</tr>`,
      )
      .join('')

    const excelHtml = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:x="urn:schemas-microsoft-com:office:excel"
        xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
          <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Employee Detailed Data</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
          <style>
            table{border-collapse:collapse;width:100%;}
            td,th{border:1px solid #cfd8e3;padding:6px 8px;vertical-align:middle;}
            th{background:#1F4E78;color:#fff;font-weight:700;text-align:center;}
          </style>
        </head>
        <body>
          <table>
            <thead><tr>${headerRow}</tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>
    `

    const excelBlob = new Blob([excelHtml], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    })
    lzDownloadBlobFile(
      excelBlob,
      `Employee_Detailed_Data_Report_${moment().format('YYYY_MM_DD_HH_mm_ss')}.xls`,
    )
  }

  return (
    <>
      <Checkbox
        checked={checkedList.length === options.length}
        onChange={(ev) => handleSelect(ev)}
      >
        Select All
      </Checkbox>
      <Checkbox.Group
        value={checkedList}
        options={options}
        onChange={(value) => setCheckedList(value)}
      />

      <div style={{ margin: '12px 0' }}>
        {canExport && (
          <Button
            type="primary"
            disabled={checkedList.length === 0 || reportData.length === 0}
            onClick={exportToExcel}
            style={{ float: 'right', margin:'5px' }}
          >
            Export to Excel
          </Button>
        )}
      </div>

      <Table
        columns={newColumns}
        dataSource={reportData}
        style={{ marginTop: 24 }}
        pagination={false}
        scroll={{ x: tableScrollX }}
        sticky={{ offsetHeader: 0 }}
        virtual={true}
      />
    </>
  )
}
